import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Input } from '@lt/components';
import { isSSR } from 'src/helpers/ssr';
import Headline from './Headline';
import PriceSelectorContainer from '../containers/PriceSelectorContainer';
import DesignSelectorContainer from '../containers/DesignSelectorContainer';
import CertificateFAQContainer from '../containers/CertificateFAQContainer';
import CertificateContainer from '../containers/CertificateContainer';
import withMobile from '../hocs/withMobile';
import PaymentContainer from '../containers/PaymentContainer';
import ShippingTypeContainer from '../containers/ShippingTypeContainer';
import { FatBlockContainer } from './ContentContainer';

const ClientFormContainer = lazy(() =>
  import(
    /* webpackChunkName: "ClientFormContainer" */ '../containers/ClientFormContainer'
  ),
);

const GiftCertificateForm = ({
  setHolder,
  setText,
  isClientConfirmed,
  isCreated,
  isMobile,
}) => {
  // eslint-disable-next-line no-use-before-define
  const PaymentBlockWrapper = isMobile
    ? StyledFatBlockContainer
    : React.Fragment;
  return (
    <StyledContainer isMobile={isMobile}>
      <Headline>Выберите сумму сертификата</Headline>
      <PriceSelectorContainer />
      <Headline>Выберите дизайн сертификата</Headline>
      {isMobile && <StyledCertificateContainer width={290} />}
      <DesignSelectorContainer disabled={isCreated} />
      <Headline>Кому</Headline>
      <Input
        disabled={isCreated}
        placeholder="Укажите имя"
        onChange={(e) => setHolder(e.target.value)}
        maxLength={60}
      />
      <Headline>Добавьте поздравление</Headline>
      <Input
        disabled={isCreated}
        as="textarea"
        placeholder="Максимум 200 символов"
        onChange={(e) => setText(e.target.value)}
        maxLength={200}
      />
      <Headline>Выберите способ доставки</Headline>
      <ShippingTypeContainer disabled={isCreated} />

      {isClientConfirmed && <Headline>Оплатите сертификат</Headline>}

      {!isSSR && (
        <Suspense fallback={<div />}>
          <PaymentBlockWrapper>
            <StyledPaymentInfoBlock isMobile={isMobile}>
              <SwitchTransition>
                <CSSTransition
                  key={isClientConfirmed ? 'client' : 'payment'}
                  classNames="payment-transition"
                  timeout={150}
                >
                  {!isClientConfirmed ? (
                    <ClientFormContainer />
                  ) : (
                    <PaymentContainer />
                  )}
                </CSSTransition>
              </SwitchTransition>
            </StyledPaymentInfoBlock>
          </PaymentBlockWrapper>
        </Suspense>
      )}
      <StyledCertificateFAQContainer />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  --form-width: 425px;
  --font-size: 18px;
  --text-area-height: 100px;

  ${(props) =>
    props.isMobile &&
    css`
      --form-width: 290px;
      --font-size: 14px;
      --text-area-height: 200px;
      align-self: center;
    `};

  display: flex;
  width: var(--form-width);
  flex-direction: column;

  > input,
  > textarea {
    font-size: var(--font-size);
    font-weight: bold;
  }

  textarea {
    min-height: var(--text-area-height);
  }
`;

const StyledFatBlockContainer = styled(FatBlockContainer)`
  border-radius: 6px;
  box-shadow: 0 10px 15px 0 rgba(216, 214, 204, 0.9),
    0 1px 2px 0 rgba(210, 208, 197, 0.9);
`;

const StyledCertificateContainer = styled(CertificateContainer)`
  margin-bottom: 20px;
  align-self: center;
  box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.2);
`;

const StyledPaymentInfoBlock = styled.div`
  padding: ${(props) => (props.isMobile ? '10px' : '20px')};
  overflow: visible;
  background: ${(props) => props.theme.colors.veryLightPink};
  border-radius: 4px;

  .payment-transition-enter {
    opacity: 0.5;
    transform: translateX(-100%);
  }

  .payment-transition-enter-active {
    opacity: 1;
    transition: opacity 300ms, transform 300ms;
    transform: translateX(0);
  }

  .payment-transition-exit {
    opacity: 1;
    transform: translateX(0);
  }

  .payment-transition-exit-active {
    opacity: 0.5;
    transition: opacity 300ms, transform 300ms;
    transform: translateX(100%);
  }
`;

const StyledCertificateFAQContainer = styled(CertificateFAQContainer)`
  margin-top: ${(props) => (props.isMobile ? '20px' : '30px')};
`;

GiftCertificateForm.propTypes = {
  setHolder: PropTypes.func.isRequired,
  setText: PropTypes.func.isRequired,
  isClientConfirmed: PropTypes.bool.isRequired,
  isCreated: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
};
GiftCertificateForm.defaultProps = {
  isMobile: false,
};

export default React.memo(withMobile(GiftCertificateForm));
